/**
 * Authentication context, hooks, and API helpers for the landing site.
 *
 * Token is stored in localStorage so it survives page reloads.
 * The same JWT works for both the landing site and the desktop app.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'
import { apiFetch, API_BASE_URL } from './api'

// Re-export API_BASE_URL for convenience
export { API_BASE_URL }

// ─── Types ────────────────────────────────────────────────────

export interface UserProfile {
  user_id: string
  email: string
  is_email_verified: boolean
  created_at: string | null
  plan: string
  subscription_status: string
  trial_seconds_used: number
  trial_seconds_limit: number
  trial_seconds_remaining: number
  is_paid: boolean
}

interface RegisterRequest {
  email: string
  password: string
  device_fingerprint?: string
}

interface RegisterResponse {
  user_id: string
  email: string
  access_token: string
  token_type: string
  trial_seconds_limit: number
}

interface LoginRequest {
  email: string
  password: string
}

interface TokenResponse {
  access_token: string
  token_type: string
}

// ─── Token helpers ────────────────────────────────────────────

const TOKEN_KEY = 'auth_token'

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

// ─── Authenticated fetch ──────────────────────────────────────

/**
 * Wrapper around apiFetch that injects the Authorization header
 * when a token is available.
 */
export async function authFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getStoredToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const url = `${API_BASE_URL}${endpoint}`
  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    // Handle FastAPI validation errors (422) which return array of errors
    let message = `HTTP ${response.status}`
    if (error.detail) {
      if (Array.isArray(error.detail)) {
        // Validation error - extract first error message
        message = error.detail.map((e: { msg?: string; loc?: string[] }) =>
          e.msg || JSON.stringify(e)
        ).join(', ')
      } else {
        message = error.detail
      }
    }
    throw new Error(message)
  }

  // Handle 204 No Content (e.g. DELETE responses)
  if (response.status === 204) {
    return undefined as unknown as T
  }

  return response.json()
}

// ─── Auth API calls ───────────────────────────────────────────

export async function apiRegister(data: RegisterRequest): Promise<RegisterResponse> {
  const res = await apiFetch<RegisterResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  setStoredToken(res.access_token)
  return res
}

export async function apiLogin(data: LoginRequest): Promise<TokenResponse> {
  const res = await apiFetch<TokenResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  })
  setStoredToken(res.access_token)
  return res
}

export async function apiGetMe(): Promise<UserProfile> {
  return authFetch<UserProfile>('/auth/me')
}

export function apiLogout(): void {
  clearStoredToken()
}

// ─── React Context ────────────────────────────────────────────

interface AuthContextValue {
  /** The authenticated user, or null if not logged in. */
  user: UserProfile | null
  /** True while the initial token validation is in progress. */
  loading: boolean
  /** Login and populate user state. */
  login: (email: string, password: string) => Promise<void>
  /** Register and populate user state. */
  register: (email: string, password: string) => Promise<void>
  /** Clear local token and user state. */
  logout: () => void
  /** Re-fetch user profile (e.g. after payment). */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  // On mount, check for existing token
  useEffect(() => {
    const token = getStoredToken()
    if (!token) {
      setLoading(false)
      return
    }
    apiGetMe()
      .then(setUser)
      .catch(() => {
        // Token invalid/expired — clear it silently
        clearStoredToken()
      })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    await apiLogin({ email, password })
    const profile = await apiGetMe()
    setUser(profile)
  }, [])

  const register = useCallback(async (email: string, password: string) => {
    await apiRegister({ email, password })
    const profile = await apiGetMe()
    setUser(profile)
  }, [])

  const logout = useCallback(() => {
    apiLogout()
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const profile = await apiGetMe()
      setUser(profile)
    } catch {
      clearStoredToken()
      setUser(null)
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
